'use client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Loader2, Settings, AlertCircle, CheckCircle2, AlertTriangle, Users, RefreshCw } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import apiClient from '@/lib/noc-api-client'
import Link from 'next/link'
import { useLanguage } from '@/contexts/LanguageContext'

interface ClientInfo {
  mac: string | null
  description: string | null
  dhcp_hostname: string | null
  ip: string | null
  ssid: string | null
  vlan: string | null
}

interface DeviceResourceStats {
  device_serial: string
  device_name: string | null
  device_type: string
  model: string | null
  network_id: string | null
  network_name: string | null
  cpu_percent: number | null
  memory_percent: number | null
  client_count: number | null
  clients: ClientInfo[] | null
  status: string
}

interface ResourceStats {
  memory_avg: number
  session_total: number
  total_traffic_bytes: number
  devices: DeviceResourceStats[]
  timestamp: string
}

interface ResourceThresholds {
  ap_cpu_percent: number
  ap_memory_percent: number
  ap_client_count: number
  switch_cpu_percent: number
  switch_memory_percent: number
  switch_traffic_percent: number
}

export function ResourceMonitoringWidget() {
  const { t } = useLanguage()
  const [stats, setStats] = useState<ResourceStats | null>(null)
  const [thresholds, setThresholds] = useState<ResourceThresholds | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>(null)
  
  const fetchData = useCallback(async (isManual = false) => {
    try {
      setError(null)
      if (isManual) setRefreshing(true)
      const [statsData, thresholdsData] = await Promise.all([
        apiClient('/api/threshold-monitoring/resource-stats'),
        apiClient('/api/threshold-monitoring/resource-thresholds')
      ])
      setStats(statsData)
      setThresholds(thresholdsData)
      
      // Set first network as default tab if not already set
      if (!activeTab && statsData.devices.length > 0) {
        const firstNetworkId = statsData.devices[0].network_id || 'unknown'
        setActiveTab(firstNetworkId)
      }
    } catch (err) {
      console.error('Failed to fetch resource data:', err)
      setError('Failed to load resource data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [activeTab])
  
  useEffect(() => {
    fetchData()
    
    // Auto-refresh every 60 seconds (live client count)
    const interval = setInterval(() => {
      fetchData()
    }, 60000)
    
    return () => clearInterval(interval)
  }, [fetchData])
  
  // Get device status based on available metrics (currently only client count for APs)
  const getDeviceStatus = (device: DeviceResourceStats) => {
    if (!thresholds) return 'normal'
    
    // Select thresholds based on device type
    const cpuThreshold = device.device_type === 'AP' ? thresholds.ap_cpu_percent : thresholds.switch_cpu_percent
    const memoryThreshold = device.device_type === 'AP' ? thresholds.ap_memory_percent : thresholds.switch_memory_percent
    
    const cpuCritical = device.cpu_percent !== null && device.cpu_percent >= cpuThreshold
    const memoryCritical = device.memory_percent !== null && device.memory_percent >= memoryThreshold
    
    // For APs, check client count (this is real data from API)
    const clientCritical = device.device_type === 'AP' && 
      device.client_count !== null && 
      device.client_count >= thresholds.ap_client_count
    
    const cpuWarning = device.cpu_percent !== null && device.cpu_percent >= cpuThreshold * 0.8
    const memoryWarning = device.memory_percent !== null && device.memory_percent >= memoryThreshold * 0.8
    const clientWarning = device.device_type === 'AP' && 
      device.client_count !== null && 
      device.client_count >= thresholds.ap_client_count * 0.8
    
    if (cpuCritical || memoryCritical || clientCritical) return 'critical'
    if (cpuWarning || memoryWarning || clientWarning) return 'warning'
    
    // If no data available, show as normal (not critical/warning without evidence)
    return 'normal'
  }
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'critical':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
            <AlertCircle className="h-3 w-3" />
            Critical
          </span>
        )
      case 'warning':
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
            <AlertTriangle className="h-3 w-3" />
            Warning
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
            <CheckCircle2 className="h-3 w-3" />
            Normal
          </span>
        )
    }
  }
  
  const getMetricStatusColor = (value: number | null, threshold: number) => {
    if (value === null) return '#E5E7EB'
    if (value >= threshold) return '#CD585A' // Red
    if (value >= threshold * 0.8) return '#F5A623' // Yellow
    return '#1CA700' // Green
  }
  
  const getDeviceThresholds = (device: DeviceResourceStats) => {
    if (!thresholds) return { cpu: 80, memory: 85 }
    
    if (device.device_type === 'AP') {
      return {
        cpu: thresholds.ap_cpu_percent,
        memory: thresholds.ap_memory_percent,
        clients: thresholds.ap_client_count
      }
    } else {
      return {
        cpu: thresholds.switch_cpu_percent,
        memory: thresholds.switch_memory_percent
      }
    }
  }
  
  // Group devices by network
  const groupDevicesByNetwork = (devices: DeviceResourceStats[]) => {
    const grouped = new Map<string, DeviceResourceStats[]>()
    
    devices.forEach(device => {
      const networkKey = device.network_id || 'unknown'
      if (!grouped.has(networkKey)) {
        grouped.set(networkKey, [])
      }
      grouped.get(networkKey)!.push(device)
    })
    
    return Array.from(grouped.entries()).map(([networkId, devices]) => ({
      networkId,
      networkName: devices[0]?.network_name || 'Unknown Network',
      devices: devices.sort((a, b) => {
        const statusA = getDeviceStatus(a)
        const statusB = getDeviceStatus(b)
        const statusOrder = { critical: 0, warning: 1, normal: 2 }
        if (statusA !== statusB) {
          return statusOrder[statusA as keyof typeof statusOrder] - statusOrder[statusB as keyof typeof statusOrder]
        }
        return (a.device_name || a.device_serial).localeCompare(b.device_name || b.device_serial)
      })
    }))
  }
  
  const getFilteredDevices = (): DeviceResourceStats[] => {
    if (!stats?.devices || !activeTab) return []
    return stats.devices.filter(d => d.network_id === activeTab)
  }

  const networkGroups = stats?.devices ? groupDevicesByNetwork(stats.devices) : []
  const filteredDevices = getFilteredDevices()

  // Count devices by status
  const statusCounts = filteredDevices.reduce((acc, device) => {
    const status = getDeviceStatus(device)
    acc[status] = (acc[status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  // Live client count for the CURRENT TAB: sum of all AP client_counts in the visible rows.
  // This is exactly what you see when you add up the numbers in the rows manually.
  const tabClientTotal = filteredDevices
    .filter(d => d.device_type === 'AP' && d.client_count !== null)
    .reduce((sum, d) => sum + (d.client_count ?? 0), 0)

  // All-networks unique client total (deduped by MAC in the backend)
  const allNetworksClientTotal = stats?.session_total ?? 0
  
  return (
    <Card className="border border-[#DDD] shadow-none h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between px-[20px] pt-[20px] pb-[16px]">
        <div className="flex flex-col gap-0.5">
          <CardTitle className="text-[16px] font-medium text-[#676767]">{t.settings.resourceMonitoring}</CardTitle>
          <span className="text-[10px] text-[#A4A4A4]">{t.resourceMonitoringLabels.subtitle}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing || loading}
            className="p-1.5 rounded-[6px] hover:bg-[#F5F5F5] transition-colors disabled:opacity-50"
            title="Refresh now"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-[#939393] ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <Link href="/settings/resource-thresholds">
            <Settings className="text-[#676767] h-4 w-4 hover:text-[#4A5565] cursor-pointer" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="px-[20px] pb-[20px] flex-1 flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-500 text-sm">
            {error}
          </div>
        ) : (
          <div className="space-y-3 flex-1 flex flex-col">
            {/* Status Summary */}
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              <span className="text-[#676767]">Devices: <span className="font-medium text-[#141414]">{filteredDevices.length}</span></span>
              {(stats?.memory_avg ?? 0) > 0 && (
                <span className="text-[#676767]">• Avg Memory: <span className="font-medium text-[#141414]">{stats!.memory_avg.toFixed(1)}%</span></span>
              )}
              {/* Current-tab AP client sum — matches exactly what you count in the rows */}
              {tabClientTotal > 0 && (
                <span className="text-[#676767]" title={t.resourceMonitoringLabels.wirelessOnly}>
                  {'• '}
                  <span className="text-[9px] font-medium text-[#535EA4] bg-[#EEF2FF] px-1.5 py-0.5 rounded-full">
                    {t.resourceMonitoringLabels.clientsThisNetwork}
                  </span>
                  {': '}
                  <span className="font-medium text-[#141414]">{tabClientTotal}</span>
                </span>
              )}
              {/* All-networks unique total */}
              {allNetworksClientTotal > 0 && networkGroups.length > 1 && (
                <span className="text-[#676767]" title={t.resourceMonitoringLabels.wirelessOnly}>
                  {'• '}
                  <span className="text-[9px] font-medium text-[#6B7280] bg-[#F3F4F6] px-1.5 py-0.5 rounded-full">
                    {t.resourceMonitoringLabels.allNetworks}
                  </span>
                  {': '}
                  <span className="font-medium text-[#141414]">{allNetworksClientTotal}</span>
                </span>
              )}
              {statusCounts.critical > 0 && (
                <span className="text-red-600 font-medium">• {statusCounts.critical} Critical</span>
              )}
              {statusCounts.warning > 0 && (
                <span className="text-yellow-600 font-medium">• {statusCounts.warning} Warning</span>
              )}
            </div>
            
            {/* Network Tabs */}
            <div className="flex gap-2 overflow-x-auto border-b border-[#E5E7EB] pb-2">
              {networkGroups.map(({ networkId, networkName, devices }) => (
                <button
                  key={networkId}
                  onClick={() => setActiveTab(networkId)}
                  className={`px-3 py-1.5 text-[11px] font-medium rounded whitespace-nowrap transition-colors ${
                    activeTab === networkId
                      ? 'bg-[#8AA2DF] text-white'
                      : 'bg-[#FAFAFA] text-[#676767] hover:bg-[#F0F0F0]'
                  }`}
                >
                  {networkName} ({devices.length})
                </button>
              ))}
            </div>
            
            {/* Device List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {filteredDevices.length === 0 ? (
                <div className="text-center py-8 text-[11px] text-[#676767]">
                  No devices found
                </div>
              ) : (
                filteredDevices.map((device) => {
                  const deviceStatus = getDeviceStatus(device)
                  const deviceThresholds = getDeviceThresholds(device)
                  const clientColor = device.client_count !== null && 'clients' in deviceThresholds
                    ? getMetricStatusColor(device.client_count, deviceThresholds.clients!)
                    : '#E5E7EB'
                  
                  return (
                    <div 
                      key={device.device_serial} 
                      className="bg-[#FAFAFA] rounded-lg p-3 border border-[#E5E7EB] hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-[12px] font-medium text-[#141414] truncate">
                              {device.device_name || 'Unknown Device'}
                            </div>
                            <span className="text-[9px] text-[#A4A4A4] font-mono">
                              {device.device_type}
                            </span>
                          </div>
                        </div>
                        {getStatusBadge(deviceStatus)}
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        {/* CPU % via SNMP */}
                        <div className="space-y-1">
                          <span className="text-[9px] font-medium text-[#676767] uppercase tracking-wide">CPU</span>
                          {device.cpu_percent !== null ? (
                            <>
                              <div className="text-[16px] font-semibold text-[#141414]">
                                {device.cpu_percent.toFixed(1)}<span className="text-[9px] font-normal text-[#A4A4A4]">%</span>
                              </div>
                              <div className="w-full h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(device.cpu_percent, 100)}%`,
                                    backgroundColor: getMetricStatusColor(device.cpu_percent, deviceThresholds.cpu)
                                  }}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="text-[9px] text-[#A4A4A4] italic leading-tight pt-1">
                              Enable<br/>SNMP
                            </div>
                          )}
                        </div>

                        {/* Memory % (Meraki API or SNMP) */}
                        <div className="space-y-1">
                          <span className="text-[9px] font-medium text-[#676767] uppercase tracking-wide">Memory</span>
                          {device.memory_percent !== null ? (
                            <>
                              <div className="text-[16px] font-semibold text-[#141414]">
                                {device.memory_percent.toFixed(1)}<span className="text-[9px] font-normal text-[#A4A4A4]">%</span>
                              </div>
                              <div className="w-full h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-300"
                                  style={{
                                    width: `${Math.min(device.memory_percent, 100)}%`,
                                    backgroundColor: getMetricStatusColor(device.memory_percent, deviceThresholds.memory)
                                  }}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="text-[9px] text-[#A4A4A4] italic leading-tight pt-1">
                              No<br/>data
                            </div>
                          )}
                        </div>

                        {/* Clients (APs) / Active Ports (Switches) */}
                        <div className="space-y-1">
                          <span className="text-[9px] font-medium text-[#676767] uppercase tracking-wide">
                            {device.device_type === 'AP' ? 'Clients' : 'Ports'}
                          </span>
                          {device.client_count !== null ? (
                            <>
                              <div className="text-[16px] font-semibold text-[#141414]">
                                {device.device_type === 'AP' && device.clients && device.clients.length > 0 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        className="hover:underline decoration-dotted underline-offset-2 cursor-pointer focus:outline-none"
                                        style={{ color: clientColor !== '#E5E7EB' ? clientColor : '#141414' }}
                                      >
                                        {device.client_count}
                                        {deviceThresholds.clients && (
                                          <span className="text-[9px] font-normal text-[#A4A4A4]">/{deviceThresholds.clients}</span>
                                        )}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      side="left"
                                      align="start"
                                      className="w-64 p-0 shadow-lg border border-[#E5E7EB]"
                                    >
                                      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E7EB] bg-[#FAFAFA]">
                                        <Users className="h-3.5 w-3.5 text-[#676767]" />
                                        <span className="text-[11px] font-semibold text-[#141414]">
                                          {device.client_count} {device.client_count === 1 ? 'Client' : 'Clients'}
                                        </span>
                                      </div>
                                      <ul className="max-h-48 overflow-y-auto divide-y divide-[#F3F4F6]">
                                        {device.clients.map((client, idx) => {
                                          const displayName =
                                            client.description ||
                                            client.dhcp_hostname ||
                                            client.mac ||
                                            'Unknown'
                                          return (
                                            <li key={client.mac ?? idx} className="px-3 py-2">
                                              <div className="text-[11px] font-medium text-[#141414] truncate" title={displayName}>
                                                {displayName}
                                              </div>
                                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                {client.ip && (
                                                  <span className="text-[10px] text-[#676767] font-mono">{client.ip}</span>
                                                )}
                                                {client.ssid && (
                                                  <span className="text-[10px] text-[#8AA2DF] truncate max-w-[100px]" title={client.ssid}>
                                                    {client.ssid}
                                                  </span>
                                                )}
                                              </div>
                                            </li>
                                          )
                                        })}
                                      </ul>
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <>
                                    <span style={{ color: clientColor !== '#E5E7EB' ? clientColor : '#141414' }}>
                                      {device.client_count}
                                    </span>
                                    {device.device_type === 'AP' && deviceThresholds.clients && (
                                      <span className="text-[9px] font-normal text-[#A4A4A4]">/{deviceThresholds.clients}</span>
                                    )}
                                  </>
                                )}
                              </div>
                              {device.device_type === 'AP' && deviceThresholds.clients && (
                                <div className="w-full h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all duration-300"
                                    style={{
                                      width: `${Math.min((device.client_count / deviceThresholds.clients) * 100, 100)}%`,
                                      backgroundColor: clientColor
                                    }}
                                  />
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-[9px] text-[#A4A4A4] italic leading-tight pt-1">No data</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            
            {/* Last Updated */}
            <div className="text-[9px] text-[#A4A4A4] text-center pt-2 border-t border-[#E5E7EB] flex items-center justify-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
              Real-time{stats?.timestamp ? ` · ${new Date(stats.timestamp).toLocaleTimeString()}` : ''} · auto-refreshes every 60 s
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
